import joi from 'joi'
import { isValidPhoneNumber } from 'react-phone-number-input'

import i18 from '@hapana/shared/web/i18n/index'

import { internationalRegexToAllowedChars, regexToAllowedChars } from './string.util'
import {
  ABOUT_MAX_LENGTH,
  APP_VERSION_REGEX,
  APP_VERSION_MAX_LENGTH,
  PASSWORD_MIN_LENGTH as SHARED_PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH as SHARED_PASSWORD_MAX_LENGTH,
  PASSWORD_UPPERCASE_REGEX,
  PASSWORD_SPECIAL_CHARACTERS_REGEX,
} from '../constants/validations'

export { ABOUT_MAX_LENGTH, APP_VERSION_MAX_LENGTH }

// Re-export isValidPhoneNumber for external use
export { isValidPhoneNumber }

export const PATTERNS = {
  ALPHANUMERIC_ONLY: /^[a-z0-9'\-,./ ]+$/i,
  ALPHANUMERIC_SPECIAL_CHARS: /^[a-zA-Z0-9!@#$%^&*(),.?:{}<>';_+="[\]\-\\/]+$/i,
  NUMERIC_INTEGER: /^[0-9]+$/,

  WEBSITE_URL: /^((http:|https:)\/\/[^\s]+(\.[^\s]+)*(\/[^\s]*)*)?$/i,
  REDIRECT_URL: /^(?:((http:|https:)\/\/[^\s]+(\.[^\s]+)*(\/[^\s]*)*))?$/i,
  VALID_SEARCH_CHARS: /^[ A-Za-z0-9_@./+-]*$/i,
  // create person name pattern that allows letters and ' character and whitespace character
  PERSON_NAME: /^[a-zA-Z' ]+$/i,
  // create person last name pattern that allows letters and ' and - characters
  PERSON_LAST_NAME: /^[a-zA-Z' -]+$/i,
  // create password pattern that allows the following characters !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~
  PASSWORD: /^[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~\\]+$/i,
  // create email pattern that allows letters and the followiung characters -_.@+
  EMAIL: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  UPPERCASE: PASSWORD_UPPERCASE_REGEX,
  SPECIAL_CHARACTERS: PASSWORD_SPECIAL_CHARACTERS_REGEX,
  // include all normal characters plus ' - , and . characters
  GENERIC_NAME: /^[a-zA-Z0-9'\-,.:// ]+$/i,
  SMS_PUSH_NOTIFICATION: /^[a-zA-Z0-9;/ ]+$/i,
  TEMPLATE: /^[a-z0-9'\-,.|()/ ]+$/i,
  SUBJECT: /^[a-z0-9'\-,.<>/ ]+$/i,
  APHA_NUM_SPL: /^[-a-z0-9',. ]+$/i,
  // Alphanumeric without whitespace
  ALPHANUMERIC_SPECIAL_WITHOUT_WHITESPACE: /^[a-z0-9'\-_,.]+$/i,
  ALPHANUMERIC_SPECIAL_WITH_WHITESPACE: /^[a-z0-9',.\- ]+$/i,
  STREET_ADDRESS: /^[a-z0-9',.\-/ ]+$/i,
  // Only ' - , . characters
  ONLY_SPECIAL_CHARS: /^['-,.]+$/i,
  // Only alphanumeric values
  ONLY_ALPHANUMERIC: /^[a-z0-9_]+$/i,
  // Don't allow whitespace at the start of end
  NO_WHITESPACE_AT_START_END: /^\S$|^\S[\s\S]*\S$/,
  URL_PREFIX: /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/,
  ALPHNUMERIC_SPECIAL_WHITESPACE_IN_BETWEEN: /^[a-zA-Z0-9',-]+(?:\s[a-zA-Z0-9',-]+)*$/,
  // Only # and alphanumeric values
  ONLY_ALPHANUMERIC_AND_HASH: /^[a-z0-9#]+$/i,
  // Only valid hexadecimal color values
  HEXADECIMAL_COLOR: /^#[A-Fa-f0-9]{6}/i,
  POLICY_NAME: /^[a-zA-Z0-9 )(.',-]+$/i,
  // Allowlist for business/location names: letters, digits, space and a curated set of
  // punctuation that appears in real names — & ' , . ! ( ) / + - . Injection-prone chars
  // (< > " ; { } = *) stay excluded since names render into HTML, agreements, emails and
  // CSV exports. OC-206.
  LOCATION_NAME: /^[a-zA-Z0-9&'.,!()/+-]+(?:\s[a-zA-Z0-9&'.,!()/+-]+)*$/,
  // Same curated punctuation allowlist as LOCATION_NAME (& ' , . ! ( ) / + -) plus Unicode
  // letters; must start with a letter, injection-prone chars stay excluded. OC-206.
  IN_APP_NAME: /^[\p{L}\p{M}]+[\p{L}\p{M}\d\p{Zs}.',&!()/+-]*[\p{L}\p{M}\d.',&!()/+-]*$/u,
  GROW_CRM: /^[a-zA-Z0-9',-.]+(?:[a-zA-Z0-9',-.]+)*$/,
  ABOUT: /^[^<>{}]+$/,
  // Allows only  ._ and alphanumeric
  INSTAGRAM_HANDLE: /^[a-zA-Z0-9_.@]+$/i,
  BRAND_INTEGRATION_OTHER_DIALOG_RULES: /^[A-Za-z0-9.,'-]*$/i,
  GROW_HQ_FIELDS: /^[A-Za-z0-9.,'-]+(?:\s[A-Za-z0-9.,'-]+)*$/i,
  // Alphanumeric with whitespace
  ALPHANUMERIC_WITH_WHITESPACE: /^[a-z0-9 ]+$/i,
  // No whitespace
  NO_WHITESPACE: /^\S+$/i,
  FEE_CONFIGURATION_NAME_DESCRIPTION_PATTERN: /^[A-Za-z0-9.,'-]+(?:\s[A-Za-z0-9.,'-]+)*$/i,
  WEBHOOK_TYPE_PATTERN: /^[A-Za-z0-9.,'-]+(?:\s[A-Za-z0-9.,'-]+)*$/i,
  FROM_NAME: /^(?=.*[a-zA-Z])[a-zA-Z0-9 +\-&_]+$/i,
  PHONE_NUMBER: /^(\+)?[0-9]+$/i,
  INTERNATIONAL_PHONE_NUMBER: /^\+[0-9]+$/,
  APP_VERSION: APP_VERSION_REGEX,
  WEBHOOK_HEADER_KEY: /^[a-zA-Z0-9-_]+$/i,
  NO_TILDE_AND_GRACE_ACCENT: /^[^~`]*$/,
  DECIMAL_NUMBER: /^[0-9]\d*(\.\d+)?$/,
  PAGE_LINK: /\.page\.link$/,
  EMAIL_SUBJECT: /^[\p{L}\p{N}!@#$%^&*()|><?:\-_ ]+$/u,
}
export const PERSON_NAME_MAX_LENGTH = 50
export const TEMPLATE_NAME_MAX_LENGTH = 255
export const PERSON_LAST_NAME_MAX_LENGTH = 50
export const POLICY_NAME_MAX_LENGTH = 65
export const COMPANY_NAME_MAX_LENGTH = 50
export const COMPANY_NAME_MIN_LENGTH = 3
export const API_KEY_MAX_LENGTH = 128
export const API_KEY_MIN_LENGTH = 3
export const DESCRIPTION_MAX_LENGTH = 200
export const LINK_LABEL_MAX_LENGTH = 50
export const PASSWORD_MIN_LENGTH = SHARED_PASSWORD_MIN_LENGTH
export const PASSWORD_MAX_LENGTH = SHARED_PASSWORD_MAX_LENGTH
export const NEW_PASSWORD_CODE_FIELD_LENGHT = 6
export const SMS_LENGTH = 1000
export const PUSH_NOTIFICATION_TITLE = 100
export const WEBSITE_URL_MAX_LENGTH = 255
export const BRAND_NAME_MAX_LENGTH = 32
export const COLOR_LENGTH = 7
export const LOCATION_NAME_MAX_LENGTH = 32
export const IN_APP_NAME_MAX_LENGTH = 50
export const HAPANA_CLIENT_FEE_TEMPLATE_NAME_MAX_LENGTH = 50
export const GROW_CRM_MAX_LENGTH = 70
export const INSTAGRAM_HANDLE_MAX_LENGTH = 50
export const BRAND_INTEGRATION_OTHER_DIALOG_MAX_LENGTH = 255
export const GROW_HQ_MAX_LENGTH = 70
// Cap matches the Firebase Auth tenant displayName limit (20 chars) minus
// the 2-char `-s` / `-c` suffix used when provisioning brand tenants.
// Anything over 18 fails at Firebase tenant creation time (OC-90 follow-up).
// The DB column and the public-lookup regex are intentionally wider (30) so
// any already-provisioned brands above 18 keep resolving on login.
export const URL_PREFIX_MAX_LENGTH = 18
export const GYM_PASS_MAX_LENGTH = 50
export const SPIVI_SOURCE_NAME_MAX_LENGTH = 50
export const SPIVI_REST_FIELDS_MAX_LENGTH = 70
export const COORDINATES_DECIMAL_LENGTH = 8
export const PAYOUT_AND_CURRENCY_CONVERSION_DECIMAL_LENGTH = 2
export const FEE_CONFIGURATION_NAME_FIELD_MAX_LENGTH = 50
export const FEE_CONFIGURATION_DESCRIPTION_FIELD_MAX_LENGTH = 100
export const WEBHOOK_TYPE_MAX_LENGTH = 50
export const FROM_NAME_MAX_LENGTH = 11
export const WEBHOOK_URL_MAX_LENGTH = 500
export const WEBHOOK_HEADERS_MAX_LENGTH = 249
export const STRIPE_LOCATION_ID_MAX_LENGTH = 40

export const PASSWORD_WITH_HELPER_OBJECT = {
  minLength: {
    key: 'string.min',
    message: i18.t('errors:forms.minLengthHelper', {
      count: PASSWORD_MIN_LENGTH,
    }),
  },
  maxLength: {
    key: 'string.max',
    message: i18.t('errors:forms.maxLengthHelper', {
      count: PASSWORD_MAX_LENGTH,
    }),
  },
  uppercase: {
    key: 'string.pattern.name',
    name: 'uppercase',
    message: i18.t('errors:forms.helper.uppercase'),
  },
  specialCharacters: {
    key: 'string.pattern.name',
    name: 'specialCharacters',
    message: i18.t('errors:forms.helper.specialCharacters'),
  },
}

export const rules = {
  minLength: (minLengthValue) => ({
    value: minLengthValue,
    message: i18.t('errors:forms.minLength', { count: minLengthValue }),
  }),
  maxLength: (maxLengthValue) => ({
    value: maxLengthValue,
    message: i18.t('errors:forms.maxLength', { count: maxLengthValue }),
  }),
  required: () => ({
    value: true,
    message: i18.t('errors:forms.required'),
  }),
  pattern: (pattern, customMessage) => ({
    value: pattern,
    message: customMessage || i18.t('errors:forms.pattern'),
  }),
  isValidPhoneNumber: (phoneNumber, countryCode) =>
    isValidPhoneNumber(phoneNumber, countryCode) || i18.t('errors:forms.invalidPhone'),

  maxSize: (maxSize) => (files) => {
    const [file] = files
    return file?.size < maxSize || i18.t('errors:forms.maxSize')
  },
  acceptedFormats: (formats) => (files) => {
    const [file] = files
    return formats.includes(file.type) || i18.t('errors:forms.invalidFile')
  },
  hasDaysSelected: (hasCustomHours, selectedWeekdays) => (value) => {
    return !(value && hasCustomHours && selectedWeekdays.length === 0) || ' '
  },
}

export const SCHEMAS = {
  genericDescription: joi
    .string()
    .allow('')
    .max(DESCRIPTION_MAX_LENGTH)
    .messages({
      'string.max': i18.t('errors:forms.maxLength', {
        count: DESCRIPTION_MAX_LENGTH,
      }),
    }),

  numericInteger: joi
    .string()
    .pattern(PATTERNS.NUMERIC_INTEGER)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.pattern.base': i18.t('errors:forms.numericInteger'),
    }),

  personName: joi
    .string()
    .pattern(PATTERNS.PERSON_NAME)
    .message(
      i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.PERSON_NAME).string,
        count: regexToAllowedChars(PATTERNS.PERSON_NAME).length,
      })
    )
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(i18.t('errors:forms.noWhitespace'))
    .max(PERSON_NAME_MAX_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: PERSON_NAME_MAX_LENGTH,
      }),
    }),
  personLastName: joi
    .string()
    .pattern(PATTERNS.PERSON_LAST_NAME)
    .message(
      i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.PERSON_LAST_NAME).string,
        count: regexToAllowedChars(PATTERNS.PERSON_LAST_NAME).length,
      })
    )
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(i18.t('errors:forms.noWhitespace'))
    .max(PERSON_LAST_NAME_MAX_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: PERSON_LAST_NAME_MAX_LENGTH,
      }),
    }),
  companyName: joi
    .string()
    .pattern(PATTERNS.ALPHANUMERIC_SPECIAL_WITHOUT_WHITESPACE)
    .max(COMPANY_NAME_MAX_LENGTH)
    .min(COMPANY_NAME_MIN_LENGTH)
    .messages({
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITHOUT_WHITESPACE).string,
        count: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITHOUT_WHITESPACE).length,
      }),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: COMPANY_NAME_MAX_LENGTH,
      }),
      'string.min': i18.t('errors:forms.minLength', {
        count: COMPANY_NAME_MIN_LENGTH,
      }),
    }),
  transactionLimit: joi
    .string()
    .required()
    .pattern(PATTERNS.DECIMAL_NUMBER)
    .messages({
      'string.pattern.base': i18.t('errors:forms.transactionLimitError'),
      'string.empty': i18.t('errors:forms.required'),
    }),
  urlPrefix: joi
    .string()
    .pattern(PATTERNS.URL_PREFIX)
    .max(URL_PREFIX_MAX_LENGTH)
    .required()
    .messages({
      'string.pattern.base': i18.t('errors:forms.uppercaseNotAllowed'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: URL_PREFIX_MAX_LENGTH,
      }),
    }),
  url: joi
    .string()
    .pattern(PATTERNS.WEBSITE_URL)
    .max(WEBSITE_URL_MAX_LENGTH)
    .required()
    .messages({
      'string.pattern.base': i18.t('errors:forms.websiteURLError'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: WEBSITE_URL_MAX_LENGTH,
      }),
    }),
  webhookUrl: joi
    .string()
    .pattern(PATTERNS.WEBSITE_URL)
    .max(WEBHOOK_URL_MAX_LENGTH)
    .required()
    .messages({
      'string.pattern.base': i18.t('errors:forms.pattern'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: WEBHOOK_URL_MAX_LENGTH,
      }),
    }),
  redirectUrl: joi
    .string()
    .pattern(PATTERNS.REDIRECT_URL)
    .max(WEBSITE_URL_MAX_LENGTH)
    .allow('')
    .messages({
      'string.pattern.base': i18.t('errors:forms.pattern'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: WEBSITE_URL_MAX_LENGTH,
      }),
    }),
  stripeTerminal: joi
    .string()
    .max(STRIPE_LOCATION_ID_MAX_LENGTH)
    .pattern(PATTERNS.NO_WHITESPACE)
    .pattern(PATTERNS.NO_TILDE_AND_GRACE_ACCENT)
    .messages({
      'string.pattern.base': i18.t('errors:forms.invalidStripe'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: STRIPE_LOCATION_ID_MAX_LENGTH,
      }),
    }),
  password: joi
    .string()
    .max(PASSWORD_MAX_LENGTH)
    .min(PASSWORD_MIN_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: PASSWORD_MAX_LENGTH,
      }),
      'string.min': i18.t('errors:forms.minLength', {
        count: PASSWORD_MIN_LENGTH,
      }),
    }),
  email: joi
    .string()
    .pattern(PATTERNS.EMAIL)
    .messages({
      'string.pattern.base': i18.t('errors:forms.invalidEmail'),
      'string.empty': i18.t('errors:forms.required'),
    }),
  passwordWithHelper: joi
    .string()
    .pattern(PATTERNS.UPPERCASE, {
      name: PASSWORD_WITH_HELPER_OBJECT.uppercase.name,
    })
    .pattern(PATTERNS.SPECIAL_CHARACTERS, {
      name: PASSWORD_WITH_HELPER_OBJECT.specialCharacters.name,
    })
    .max(PASSWORD_MAX_LENGTH)
    .min(PASSWORD_MIN_LENGTH)
    .messages({
      'string.pattern.uppercase': PASSWORD_WITH_HELPER_OBJECT.uppercase.message,
      'string.pattern.specialCharacters_other':
        PASSWORD_WITH_HELPER_OBJECT.specialCharacters.message,
      'string.max': PASSWORD_WITH_HELPER_OBJECT.maxLength.message,
      'string.min': PASSWORD_WITH_HELPER_OBJECT.minLength.message,
    }),
  newPasswordCode: joi
    .string()
    .empty('')
    .messages({
      'string.empty': i18.t('errors:forms.required'),
    }),
  arrayOfNumbers: joi.array().items(
    joi.object({
      id: joi.string(),
      count: joi
        .number()
        .integer()
        .required()
        .min(1)
        .max(999999)
        .messages({
          'number.integer': i18.t('errors:forms.nonIntegerValue', {
            text: 'SMS count',
          }),
          'number.base': i18.t('errors:forms.requiredNumber', {
            field: 'SMS count',
          }),
          'number.min': i18.t('errors:forms.minLengthCount', {
            field: 'SMS count',
            count: '1',
          }),
          'number.max': i18.t('errors:forms.maxLengthCount', {
            field: 'SMS count',
            count: '999999',
          }),
          'number.unsafe': i18.t('errors:forms.maxLengthCount', {
            field: 'SMS count',
            count: '999999',
          }),
        }),
      price: joi
        .number()
        .required()
        .min(1)
        .max(999999)
        .precision(2)
        .messages({
          'number.base': i18.t('errors:forms.requiredNumber', {
            field: 'Price',
          }),
          'number.min': i18.t('errors:forms.minLengthCount', {
            field: 'Price',
            count: '1',
          }),
          'number.max': i18.t('errors:forms.maxLengthCount', {
            field: 'Price',
            count: '999999',
          }),
          'number.unsafe': i18.t('errors:forms.maxLengthCount', {
            field: 'Price',
            count: '999999',
          }),
        }),
    })
  ),
  stringRequired: joi.string().messages({
    'string.empty': i18.t('errors:forms.required'),
  }),
  confirmPassword: joi
    .string()
    // check if the password and the confirmation password are the same
    .valid(joi.ref('newPassword'))
    .messages({
      'any.only': i18.t('errors:forms.confirmPassword'),
    }),
  genericName: joi
    .string()
    .pattern(PATTERNS.GENERIC_NAME)
    .max(PERSON_LAST_NAME_MAX_LENGTH)
    .messages({
      'string.pattern.base': i18.t('errors:forms.pattern'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: PERSON_LAST_NAME_MAX_LENGTH,
      }),
    }),
  optional: joi.optional(),
  notEmpty: joi.string().messages({
    'string.empty': i18.t('errors:forms.required'),
  }),
  notEmptyArray: joi.array().min(1).required().messages({
    'any.required': 'This field is required.',
  }),
  phone: joi
    .string()
    .pattern(PATTERNS.PHONE_NUMBER)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.pattern.base': i18.t('errors:forms.phone'),
    }),
  ipAddress: joi
    .string()
    .ip({
      version: ['ipv4', 'ipv6'],
    })
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.ipVersion': i18.t('errors:forms.invalidIp'),
    }),
  sms: joi
    .string()
    .max(SMS_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', { count: SMS_LENGTH }),
    }),
  pushNotificationTitle: joi
    .string()
    .max(PUSH_NOTIFICATION_TITLE)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: PUSH_NOTIFICATION_TITLE,
      }),
    }),
  pushNotificationMessage: joi
    .string()
    .max(TEMPLATE_NAME_MAX_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: TEMPLATE_NAME_MAX_LENGTH,
      }),
    }),
  templateName: joi
    .string()
    .max(TEMPLATE_NAME_MAX_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: TEMPLATE_NAME_MAX_LENGTH,
      }),
    }),
  subject: joi
    .string()
    .pattern(PATTERNS.EMAIL_SUBJECT)
    .max(TEMPLATE_NAME_MAX_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: PERSON_LAST_NAME_MAX_LENGTH,
      }),
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: internationalRegexToAllowedChars(PATTERNS.EMAIL_SUBJECT).string,
        count: internationalRegexToAllowedChars(PATTERNS.EMAIL_SUBJECT).length,
      }),
    }),
  linkLabel: joi
    .string()
    .pattern(PATTERNS.APHA_NUM_SPL)
    .max(LINK_LABEL_MAX_LENGTH)
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .messages({
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.APHA_NUM_SPL).string,
        count: regexToAllowedChars(PATTERNS.APHA_NUM_SPL).length,
      }),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: LINK_LABEL_MAX_LENGTH,
      }),
    }),
  integrationDescription: joi
    .string()
    .allow('')
    .max(DESCRIPTION_MAX_LENGTH)
    .messages({
      'string.max': i18.t('errors:forms.maxLength', {
        count: DESCRIPTION_MAX_LENGTH,
      }),
    }),
  integrationDescriptionRequired: joi
    .string()
    .max(DESCRIPTION_MAX_LENGTH)
    .messages({
      'string.max': i18.t('errors:forms.maxLength', {
        count: DESCRIPTION_MAX_LENGTH,
      }),
    }),
  integrationKey: joi
    .string()
    .pattern(PATTERNS.ONLY_ALPHANUMERIC)
    .messages({
      'string.pattern.base': i18.t('errors:forms.noSpecialCharacters'),
      'string.empty': i18.t('errors:forms.required'),
    }),
  regionName: joi
    .string()
    .pattern(PATTERNS.ALPHNUMERIC_SPECIAL_WHITESPACE_IN_BETWEEN)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.ALPHNUMERIC_SPECIAL_WHITESPACE_IN_BETWEEN).string,
        count: regexToAllowedChars(PATTERNS.ALPHNUMERIC_SPECIAL_WHITESPACE_IN_BETWEEN).length,
      }),
    }),
  color: joi
    .string()
    .pattern(PATTERNS.ONLY_ALPHANUMERIC_AND_HASH)
    .message(
      i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.ONLY_ALPHANUMERIC_AND_HASH).string,
        count: regexToAllowedChars(PATTERNS.ONLY_ALPHANUMERIC_AND_HASH).length,
      })
    )
    .pattern(PATTERNS.HEXADECIMAL_COLOR)
    .message(i18.t('errors:forms.invalidColor'))
    .max(7)
    .min(7)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', { count: COLOR_LENGTH }),
      'string.min': i18.t('errors:forms.minLength', { count: COLOR_LENGTH }),
    }),
  brandName: joi
    .string()
    .pattern(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE)
    .message(
      i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE).string,
        count: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE).length,
      })
    )
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(i18.t('errors:forms.noWhitespace'))
    .max(BRAND_NAME_MAX_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: BRAND_NAME_MAX_LENGTH,
      }),
    }),
  groupName: joi
    .string()
    .pattern(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE)
    .message(
      i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE).string,
        count: regexToAllowedChars(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE).length,
      })
    )
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(i18.t('errors:forms.noWhitespace'))
    .max(PERSON_LAST_NAME_MAX_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: PERSON_LAST_NAME_MAX_LENGTH,
      }),
    }),
  policyName: joi
    .string()
    .pattern(PATTERNS.POLICY_NAME)
    .message(
      i18.t('errors:forms.specialCharacters_other', {
        charList: regexToAllowedChars(PATTERNS.POLICY_NAME).string,
        count: regexToAllowedChars(PATTERNS.POLICY_NAME).length,
      })
    )
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(i18.t('errors:forms.noWhitespace'))
    .max(POLICY_NAME_MAX_LENGTH)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: POLICY_NAME_MAX_LENGTH,
      }),
    }),
  days: joi
    .number()
    .required()
    .integer()
    .min(0)
    .messages({
      'number.integer': i18.t('errors:forms.nonIntegerValue', {
        text: 'Days',
      }),
      'number.base': i18.t('errors:forms.requiredNumber', { field: 'Days' }),
    }),
  locationName: joi
    .string()
    .required()
    .max(LOCATION_NAME_MAX_LENGTH)
    .pattern(PATTERNS.LOCATION_NAME)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: LOCATION_NAME_MAX_LENGTH,
      }),
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.LOCATION_NAME).string,
        count: regexToAllowedChars(PATTERNS.LOCATION_NAME).length,
      }),
    }),
  inAppName: joi
    .string()
    .required()
    .max(IN_APP_NAME_MAX_LENGTH)
    .pattern(PATTERNS.IN_APP_NAME)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: IN_APP_NAME_MAX_LENGTH,
      }),
      'string.pattern.base': i18.t('errors:forms.allSpecialCharacters', {
        count: regexToAllowedChars(PATTERNS.IN_APP_NAME).length,
      }),
    }),
  hapanaClientFeeTemplateName: joi
    .string()
    .required()
    .max(HAPANA_CLIENT_FEE_TEMPLATE_NAME_MAX_LENGTH)
    .pattern(PATTERNS.LOCATION_NAME)
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: HAPANA_CLIENT_FEE_TEMPLATE_NAME_MAX_LENGTH,
      }),
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.LOCATION_NAME).string,
        count: regexToAllowedChars(PATTERNS.LOCATION_NAME).length,
      }),
    }),
  // HAPANA_CLIENT_FEE_TEMPLATE_NAME_MAX_LENGTH
  regions: joi.array().items(
    joi
      .string()
      .required()
      .messages({
        'string.empty': i18.t('errors:form.required'),
      })
  ),
  growCrmValidation: joi
    .string()
    .max(GROW_CRM_MAX_LENGTH)
    .pattern(PATTERNS.GROW_CRM)
    .allow('')
    .messages({
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: GROW_CRM_MAX_LENGTH,
      }),
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.GROW_CRM).string,
        count: regexToAllowedChars(PATTERNS.GROW_CRM).length,
      }),
    }),
  phonePrefixValidation: joi
    .number()
    .integer()
    .min(0)
    .max(9999)
    .messages({
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '0',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '9999',
      }),
      'number.unsafe': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '9999',
      }),
    }),
  monthlySmsCreditValidation: joi
    .number()
    .min(0)
    .max(9999)
    .messages({
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '0',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '9999',
      }),
      'number.unsafe': i18.t('errors:forms.unsafe'),
    }),
  cardBankProcessingFeeVar: joi
    .number()
    .min(0)
    .max(9.99)
    .optional()
    .messages({
      'number.integer': i18.t('errors:forms.nonIntegerValue', {
        text: 'This field',
      }),
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '0',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '9.99',
      }),
      'number.unsafe': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '9.99',
      }),
    })
    .custom((value, helpers) => {
      const stringValue = value.toString()
      const [, decimalPart] = stringValue.split('.')
      const decimalNumberLength = decimalPart ? decimalPart.length : 0
      if (decimalNumberLength > 2) {
        return helpers.message(i18.t('errors:forms.twoDecimalError'))
      }
      return value
    }),
  cardBankProcessingFeeFlat: joi
    .number()
    .min(0)
    .max(99.99)
    .optional()
    .messages({
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '0',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '99.99',
      }),
      'number.unsafe': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '99.99',
      }),
    })
    .custom((value, helpers) => {
      const stringValue = value.toString()
      const [, decimalPart] = stringValue.split('.')
      const decimalNumberLength = decimalPart ? decimalPart.length : 0
      if (decimalNumberLength > 2) {
        return helpers.message(i18.t('errors:forms.twoDecimalError'))
      }
      return value
    }),
  about: joi
    .string()
    .allow('')
    .max(ABOUT_MAX_LENGTH)
    // Joi parses braces in a message as a template, so the characters travel as the pattern name.
    .pattern(PATTERNS.ABOUT, { name: '< > { }' })
    .messages({
      'string.max': i18.t('errors:forms.maxLength', {
        count: ABOUT_MAX_LENGTH,
      }),
      'string.pattern.name': i18.t('errors:forms.notAllowedCharacters', { charList: '{#name}' }),
    }),
  instagramHandle: joi
    .string()
    .allow('')
    .max(INSTAGRAM_HANDLE_MAX_LENGTH)
    .pattern(PATTERNS.INSTAGRAM_HANDLE)
    .messages({
      'string.max': i18.t('errors:forms.maxLength', {
        count: INSTAGRAM_HANDLE_MAX_LENGTH,
      }),
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.INSTAGRAM_HANDLE).string,
        count: regexToAllowedChars(PATTERNS.INSTAGRAM_HANDLE).length,
      }),
    }),
  arrayOfStrings: joi.array().items(joi.string().required()),
  brandIntegrationOtherDialogRules: joi
    .string()
    .required()
    .max(BRAND_INTEGRATION_OTHER_DIALOG_MAX_LENGTH)
    .messages({
      'string.max': i18.t('errors:forms.maxLength', {
        count: BRAND_INTEGRATION_OTHER_DIALOG_MAX_LENGTH,
      }),
      'string.empty': i18.t('errors:forms.required'),
    }),
  brandIntegrationOtherDialogRulesNotRequired: joi
    .string()
    .allow('')
    .max(BRAND_INTEGRATION_OTHER_DIALOG_MAX_LENGTH)
    .messages({
      'string.max': i18.t('errors:forms.maxLength', {
        count: BRAND_INTEGRATION_OTHER_DIALOG_MAX_LENGTH,
      }),
    }),
  customIntegrationRules: joi.array().items(
    joi
      .string()
      .required()
      .max(BRAND_INTEGRATION_OTHER_DIALOG_MAX_LENGTH)
      .pattern(PATTERNS.BRAND_INTEGRATION_OTHER_DIALOG_RULES)
      .messages({
        'string.max': i18.t('errors:forms.maxLength', {
          count: BRAND_INTEGRATION_OTHER_DIALOG_MAX_LENGTH,
        }),
        'string.empty': i18.t('errors:forms.required'),
        'string.pattern.base': i18.t('errors:forms.specialCharacters', {
          charList: regexToAllowedChars(PATTERNS.BRAND_INTEGRATION_OTHER_DIALOG_RULES).string,
          count: regexToAllowedChars(PATTERNS.BRAND_INTEGRATION_OTHER_DIALOG_RULES).length,
        }),
      })
  ),
  growHqkeyName: joi
    .string()
    .required()
    .max(GROW_HQ_MAX_LENGTH)
    .pattern(PATTERNS.GROW_HQ_FIELDS)
    .messages({
      'string.max': i18.t('errors:forms.maxLength', {
        count: GROW_HQ_MAX_LENGTH,
      }),
      'string.empty': i18.t('errors:forms.required'),
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.GROW_HQ_FIELDS).string,
        count: regexToAllowedChars(PATTERNS.GROW_HQ_FIELDS).length,
      }),
    }),
  chunkSize: joi
    .number()
    .min(0.1)
    .max(99999999.99)
    .messages({
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '0.1',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '8',
      }),
      'number.unsafe': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '8',
      }),
    }),
  growHqCode: joi
    .string()
    .required()
    .messages({
      'string.empty': i18.t('errors:forms.required'),
    }),
  gymPassValidation: joi
    .string()
    .required()
    .pattern(PATTERNS.ONLY_ALPHANUMERIC)
    .max(GYM_PASS_MAX_LENGTH)
    .messages({
      'string.pattern.base': i18.t('errors:forms.noSpecialCharacters'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: GYM_PASS_MAX_LENGTH,
      }),
    }),
  spiviSourceNameValidation: joi
    .string()
    .required()
    .pattern(PATTERNS.ONLY_ALPHANUMERIC)
    .max(SPIVI_SOURCE_NAME_MAX_LENGTH)
    .messages({
      'string.pattern.base': i18.t('errors:forms.noSpecialCharacters'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: SPIVI_SOURCE_NAME_MAX_LENGTH,
      }),
    }),
  spiviRestFieldsValidations: joi
    .string()
    .required()
    .pattern(PATTERNS.NO_WHITESPACE)
    .max(SPIVI_REST_FIELDS_MAX_LENGTH)
    .messages({
      'string.pattern.base': i18.t('errors:forms.noWhitespaceAtAll'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: SPIVI_REST_FIELDS_MAX_LENGTH,
      }),
    }),
  logitude: joi
    .number()
    .min(-180)
    .max(180)
    .messages({
      'number.integer': i18.t('errors:forms.nonIntegerValue', {
        text: 'This field',
      }),
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '-180',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '180',
      }),
      'number.unsafe': i18.t('errors:forms.longitudeError'),
    })
    .custom((value, helpers) => {
      const stringValue = value.toString()
      const [, decimalPart] = stringValue.split('.')
      const decimalNumberLength = decimalPart ? decimalPart.length : 0
      if (decimalNumberLength > COORDINATES_DECIMAL_LENGTH) {
        return helpers.message(i18.t('errors:forms.longitudeError'))
      }
      return value
    }),
  latitude: joi
    .number()
    .min(-90)
    .max(90)
    .messages({
      'number.integer': i18.t('errors:forms.nonIntegerValue', {
        text: 'This field',
      }),
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '-90',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '90',
      }),
      'number.unsafe': i18.t('errors:forms.latitudeError'),
    })
    .custom((value, helpers) => {
      const stringValue = value.toString()
      const [, decimalPart] = stringValue.split('.')
      const decimalNumberLength = decimalPart ? decimalPart.length : 0
      if (decimalNumberLength > COORDINATES_DECIMAL_LENGTH) {
        return helpers.message(i18.t('errors:forms.latitudeError'))
      }
      return value
    }),
  feeConfigurationName: joi
    .string()
    .required()
    .max(FEE_CONFIGURATION_NAME_FIELD_MAX_LENGTH)
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(i18.t('errors:forms.noWhitespace'))
    .messages({
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        count: regexToAllowedChars(PATTERNS.FEE_CONFIGURATION_NAME_DESCRIPTION_PATTERN).length,
      }),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: FEE_CONFIGURATION_NAME_FIELD_MAX_LENGTH,
      }),
    }),
  feeConfigurationDescription: joi
    .string()
    .required()
    .max(FEE_CONFIGURATION_DESCRIPTION_FIELD_MAX_LENGTH)
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(i18.t('errors:forms.noWhitespace'))
    .messages({
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        count: regexToAllowedChars(PATTERNS.FEE_CONFIGURATION_NAME_DESCRIPTION_PATTERN).length,
      }),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: FEE_CONFIGURATION_DESCRIPTION_FIELD_MAX_LENGTH,
      }),
    }),
  feeConfigurationDefaultFeeRateFlat: joi
    .number()
    .min(0)
    .max(99.99)
    .precision(2)
    .optional()
    .messages({
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '0',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '99.99',
      }),
      'number.unsafe': i18.t('errors:forms.unsafe'),
    }),
  feeConfigurationDefaultFeeRatePercent: joi
    .number()
    .min(0)
    .max(9.99)
    .precision(2)
    .optional()
    .messages({
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '0',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '9.99',
      }),
      'number.unsafe': i18.t('errors:forms.unsafe'),
    }),
  feeConfigurationWebhookType: joi
    .string()
    .pattern(PATTERNS.WEBHOOK_TYPE_PATTERN)
    .max(WEBHOOK_TYPE_MAX_LENGTH)
    .optional()
    .allow('')
    .messages({
      'string.pattern.base': i18.t('errors:forms.specialCharacters', {
        charList: regexToAllowedChars(PATTERNS.WEBHOOK_TYPE_PATTERN).string,
        count: regexToAllowedChars(PATTERNS.WEBHOOK_TYPE_PATTERN).length,
      }),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: WEBHOOK_TYPE_MAX_LENGTH,
      }),
    }),
  feeConfigurationWebhookEndpoint: joi
    .string()
    .pattern(PATTERNS.WEBSITE_URL)
    .max(WEBSITE_URL_MAX_LENGTH)
    .optional()
    .allow('')
    .messages({
      'string.pattern.base': i18.t('errors:forms.invalidUrlFormat'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: WEBSITE_URL_MAX_LENGTH,
      }),
    }),
  hapanaClientFee: joi.array().items(
    joi.object({
      id: joi.string(),
      enabled: joi.optional(),
      rate: joi
        .number()
        .precision(2)
        .required()
        .when('rateType', {
          is: 'FIXED',
          then: joi
            .number()
            .min(0)
            .required()
            .messages({
              'number.integer': i18.t('errors:forms.nonIntegerValue', {
                text: 'Field',
              }),
              'number.base': i18.t('errors:forms.required'),
              'number.min': i18.t('errors:forms.minLengthCount', {
                field: 'Field',
                count: '0',
              }),
              'number.unsafe': i18.t('errors:forms.unsafe'),
            }),
          otherwise: joi
            .number()
            .min(0)
            .max(9.99)
            .required()
            .messages({
              'number.integer': i18.t('errors:forms.nonIntegerValue', {
                text: 'Field',
              }),
              'number.base': i18.t('errors:forms.required'),
              'number.min': i18.t('errors:forms.minLengthCount', {
                field: 'Field',
                count: '0',
              }),
              'number.max': i18.t('errors:forms.maxLengthCount', {
                field: 'Field',
                count: '9.99',
              }),
              'number.unsafe': i18.t('errors:forms.unsafe'),
            }),
        }),
      rateType: joi.string().valid('FIXED', 'PERCENT').required(),
    })
  ),
  fromName: joi
    .string()
    .pattern(PATTERNS.FROM_NAME)
    .pattern(PATTERNS.NO_WHITESPACE_AT_START_END)
    .message(i18.t('errors:forms.noWhitespace'))
    .max(FROM_NAME_MAX_LENGTH)
    .messages({
      'string.pattern.base': i18.t('errors:forms.specialCharactersAndOneLetter', {
        charList: regexToAllowedChars(PATTERNS.FROM_NAME).string,
        count: regexToAllowedChars(PATTERNS.FROM_NAME).length,
      }),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: FROM_NAME_MAX_LENGTH,
      }),
    }),
  iosVersion: joi
    .string()
    .trim()
    .pattern(PATTERNS.APP_VERSION)
    .max(APP_VERSION_MAX_LENGTH)
    .messages({
      'string.pattern.base': i18.t('errors:forms.invalidFormat'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: APP_VERSION_MAX_LENGTH,
      }),
    }),
  androidVersion: joi
    .string()
    .trim()
    .pattern(PATTERNS.APP_VERSION)
    .max(APP_VERSION_MAX_LENGTH)
    .messages({
      'string.pattern.base': i18.t('errors:forms.invalidFormat'),
      'string.empty': i18.t('errors:forms.required'),
      'string.max': i18.t('errors:forms.maxLength', {
        count: APP_VERSION_MAX_LENGTH,
      }),
    }),
  payoutAndCurrentConversionFee: joi
    .number()
    .min(0.1)
    .max(9.99)
    .messages({
      'number.integer': i18.t('errors:forms.nonIntegerValue', {
        text: 'This field',
      }),
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '0.1',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '9.99',
      }),
      'number.unsafe': i18.t('errors:forms.payoutAndCurrentConversionFeeError'),
    })
    .custom((value, helpers) => {
      const stringValue = value.toString()
      const [, decimalPart] = stringValue.split('.')
      const decimalNumberLength = decimalPart ? decimalPart.length : 0
      if (decimalNumberLength > PAYOUT_AND_CURRENCY_CONVERSION_DECIMAL_LENGTH) {
        return helpers.message(i18.t('errors:forms.payoutAndCurrentConversionFeeError'))
      }
      return value
    }),
  payoutAndCurrentConversionFeeFlat: joi
    .number()
    .min(0.01)
    .max(99.99)
    .messages({
      'number.integer': i18.t('errors:forms.nonIntegerValue', {
        text: 'This field',
      }),
      'number.base': i18.t('errors:forms.requiredNumber', {
        field: 'This field',
      }),
      'number.min': i18.t('errors:forms.minLengthCount', {
        field: 'This field',
        count: '0.01',
      }),
      'number.max': i18.t('errors:forms.maxLengthCount', {
        field: 'This field',
        count: '99.99',
      }),
      'number.unsafe': i18.t('errors:forms.payoutAndCurrentConversionFeeError'),
    })
    .custom((value, helpers) => {
      const stringValue = value.toString()
      const [, decimalPart] = stringValue.split('.')
      const decimalNumberLength = decimalPart ? decimalPart.length : 0
      if (decimalNumberLength > PAYOUT_AND_CURRENCY_CONVERSION_DECIMAL_LENGTH) {
        return helpers.message(i18.t('errors:forms.payoutAndCurrentConversionFeeError'))
      }
      return value
    }),
  headerKey: joi
    .string()
    .pattern(PATTERNS.WEBHOOK_HEADER_KEY)
    .allow('')
    .messages({
      'string.pattern.base': i18.t('errors:forms.pattern'),
    }),
  headerValueOptional: joi
    .string()
    .allow('')
    .pattern(PATTERNS.ALPHANUMERIC_SPECIAL_WITH_WHITESPACE)
    .messages({
      'string.pattern.base': i18.t('errors:forms.pattern'),
    }),
  arrayOfValues: joi.array().items(
    joi
      .object({
        id: joi.string(),
        value: joi.string().required(),
      })
      .messages({ 'string.empty': i18.t('errors:forms.required') })
  ),
}
